import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Download, Search, ShieldCheck, ShieldOff, UserX, UserPlus, CalendarClock, Trash2, ChevronRight, BadgeEuro } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-[color:var(--hairline)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[color:var(--ink)] transition-colors duration-200";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const typeLabel = { full_exam: "Esame casuale", simulation: "Simulazione ufficiale", block_sim: "Sim. blocco", module_quiz: "Quiz modulo", lesson_quiz: "Quiz lezione", review_quiz: "Ripasso errori" };

export const downloadCsv = async (path, filename) => {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Export scaricato.");
  } catch (e) {
    toast.error("Export non riuscito. Riprova.");
  }
};

const emptyNewUser = { nome: "", email: "", password: "", is_admin: false, hasExpiry: false, expiresAt: "" };

export const AdminUsers = () => {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [detail, setDetail] = useState(null); // {user, moduleProgress, attempts, activity}
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // {action:'role'|'anonymize'|'delete', user, value?}
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [expiryDialog, setExpiryDialog] = useState(null); // {user, hasExpiry, expiresAt}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (roleFilter) params.set("role", roleFilter);
    api
      .get(`/admin/users?${params.toString()}`)
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Impossibile caricare gli iscritti."));
  }, [search, roleFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Impossibile caricare il dettaglio."));
    } finally {
      setDetailLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUser.email.trim() || newUser.password.length < 6) {
      toast.error("Email valida e password di almeno 6 caratteri sono obbligatorie.");
      return;
    }
    if (newUser.hasExpiry && !newUser.expiresAt) {
      toast.error("Scegli la data di scadenza oppure seleziona 'Sine die'.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", {
        email: newUser.email.trim(),
        password: newUser.password,
        nome: newUser.nome.trim(),
        is_admin: newUser.is_admin,
        expiresAt: newUser.hasExpiry ? newUser.expiresAt : null,
      });
      toast.success(newUser.hasExpiry ? `Utente creato, scade il ${new Date(newUser.expiresAt).toLocaleDateString("it-IT")}.` : "Utente creato senza scadenza (sine die).");
      setCreateOpen(false);
      setNewUser(emptyNewUser);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveExpiry = async () => {
    if (!expiryDialog) return;
    if (expiryDialog.hasExpiry && !expiryDialog.expiresAt) {
      toast.error("Scegli la data di scadenza oppure seleziona 'Sine die'.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/users/${expiryDialog.user.id}/expiry`, {
        expiresAt: expiryDialog.hasExpiry ? expiryDialog.expiresAt : null,
      });
      toast.success(expiryDialog.hasExpiry ? "Scadenza aggiornata." : "Account impostato sine die.");
      setExpiryDialog(null);
      load();
      if (detail) openDetail(expiryDialog.user.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    const { action, user, value } = confirm;
    try {
      if (action === "role") {
        await api.put(`/admin/users/${user.id}/role`, { is_admin: value });
        toast.success(value ? "Promosso ad amministratore." : "Riportato a studente.");
      } else if (action === "access") {
        await api.put(`/admin/users/${user.id}/access`, { status: value });
        toast.success(value === "paid" ? "Accesso completo attivato." : "Riportato a free.");
      } else if (action === "anonymize") {
        await api.post(`/admin/users/${user.id}/anonymize`);
        toast.success("Utente anonimizzato.");
      } else if (action === "delete") {
        await api.delete(`/admin/users/${user.id}`);
        toast.success("Utente e dati eliminati definitivamente.");
        setDetail(null);
      }
      setConfirm(null);
      load();
      if (detail && action !== "delete") openDetail(user.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------- DETAIL VIEW ----------
  if (detail || detailLoading) {
    const u = detail?.user;
    return (
      <div data-testid="admin-user-detail">
        <button
          onClick={() => setDetail(null)}
          data-testid="admin-user-detail-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Elenco iscritti
        </button>

        {detailLoading || !u ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-32 rounded-3xl" />
            <Skeleton className="h-40 rounded-3xl" />
          </div>
        ) : (
          <>
            {/* Profile */}
            <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-extrabold tracking-tight text-lg">{u.nome}</h3>
                  <p className="text-sm text-[color:var(--text-2)]">{u.email}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {u.is_admin && <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--ink)] text-white px-2.5 py-1">Admin</span>}
                  {(u.subscription?.status === "paid" || u.createdByAdmin) && !u.is_admin && (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--volt)] px-2.5 py-1" data-testid="admin-user-paid-badge">
                      {u.subscription?.status === "paid" ? "Pagato" : "Accesso admin"}
                    </span>
                  )}
                  {u.anonymized && <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] px-2.5 py-1">Anonimizzato</span>}
                  {u.expired && <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-2.5 py-1" data-testid="admin-user-expired-badge">Scaduto</span>}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
                {[
                  ["Iscritto il", fmtDate(u.createdAt)],
                  ["Ultimo accesso", fmtDate(u.lastAccessAt)],
                  ["Avanzamento", `${u.progressPercent}%`],
                  ["Scadenza account", u.expiresAt ? fmtDate(u.expiresAt) : "Sine die"],
                  ["Accesso", u.subscription?.status === "paid" ? "Pagato (39 €)" : u.createdByAdmin ? "Da admin" : "Free"],
                  ["Origine", u.createdByAdmin ? "Creato da admin" : "Registrato"],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-2xl bg-[color:var(--bg-subtle)] p-3">
                    <p className="font-mono text-sm font-semibold">{val}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-2)] font-bold mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-[color:var(--hairline)] flex flex-wrap gap-2">
                {!u.anonymized && (
                  <button
                    onClick={() => setConfirm({ action: "role", user: u, value: !u.is_admin })}
                    data-testid="admin-toggle-role-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-4 py-2 text-xs font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    {u.is_admin ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                    {u.is_admin ? "Rendi studente" : "Rendi amministratore"}
                  </button>
                )}
                {!u.anonymized && (
                  <button
                    onClick={() => setExpiryDialog({ user: u, hasExpiry: !!u.expiresAt, expiresAt: u.expiresAt ? u.expiresAt.slice(0, 10) : "" })}
                    data-testid="admin-set-expiry-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-4 py-2 text-xs font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <CalendarClock className="w-3.5 h-3.5" /> Modifica scadenza
                  </button>
                )}
                {!u.anonymized && !u.is_admin && (
                  <button
                    onClick={() => setConfirm({ action: "access", user: u, value: u.subscription?.status === "paid" ? "free" : "paid" })}
                    data-testid="admin-toggle-access-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-4 py-2 text-xs font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <BadgeEuro className="w-3.5 h-3.5" />
                    {u.subscription?.status === "paid" ? "Riporta a free" : "Imposta come pagato"}
                  </button>
                )}
                {!u.anonymized && !u.is_admin && (
                  <button
                    onClick={() => setConfirm({ action: "anonymize", user: u })}
                    data-testid="admin-anonymize-user-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-4 py-2 text-xs font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <UserX className="w-3.5 h-3.5" /> Anonimizza (GDPR)
                  </button>
                )}
                {!u.is_admin && (
                  <button
                    onClick={() => setConfirm({ action: "delete", user: u })}
                    data-testid="admin-delete-user-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-4 py-2 text-xs font-bold hover:bg-red-50 transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Elimina definitivamente
                  </button>
                )}
              </div>
            </div>

            {/* Module progress */}
            <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase mb-3">Avanzamento per modulo</h4>
              <div className="space-y-2.5" data-testid="admin-user-module-progress">
                {detail.moduleProgress.map((m) => (
                  <div key={m.id}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold truncate">{m.titolo}</p>
                      <span className="font-mono text-[11px] text-[color:var(--text-2)] shrink-0">{m.completedLessons}/{m.totalLessons}</span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-[color:var(--bg-subtle)] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${m.totalLessons ? (m.completedLessons / m.totalLessons) * 100 : 0}%`,
                          background: m.totalLessons && m.completedLessons === m.totalLessons ? "var(--volt)" : "var(--ink)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Attempts */}
            <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase mb-3">Tentativi ({detail.attempts.length})</h4>
              {detail.attempts.length === 0 && <p className="text-sm text-[color:var(--text-2)]">Nessun tentativo registrato.</p>}
              <div className="space-y-2" data-testid="admin-user-attempts">
                {detail.attempts.slice(0, 15).map((a) => {
                  const good = a.esito === "IDONEO" || a.esito === "SUPERATO";
                  return (
                    <div key={a.id} className="flex items-center justify-between gap-2 rounded-2xl border border-[color:var(--hairline)] px-3.5 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{typeLabel[a.type] || a.type}{a.blocco ? ` · B${a.blocco}` : ""}</p>
                        <p className="font-mono text-[11px] text-[color:var(--text-2)]">
                          {fmtDateTime(a.date)}
                          {a.type === "full_exam" && a.blockScores?.length === 4 && (
                            <> · {a.blockScores.map((b) => `B${b.blocco}:${b.correct}`).join(" ")}</>
                          )}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-sm font-semibold">{a.totalCorrect}/{a.totalQuestions}</p>
                        <p className={`text-[10px] font-extrabold uppercase ${good ? "" : "text-[color:var(--danger)]"}`}>
                          {good ? <span className="rounded-full bg-[color:var(--volt)] px-1.5 py-0.5">{a.esito}</span> : a.esito}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity */}
            <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase mb-3">Attività recente</h4>
              {detail.activity.length === 0 && <p className="text-sm text-[color:var(--text-2)]">Nessuna attività.</p>}
              <div className="space-y-1.5">
                {detail.activity.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm py-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${ev.type === "lesson_completed" ? "bg-[color:var(--volt)] border border-black/10" : "bg-[color:var(--ink)]"}`} />
                    <span className="flex-1 truncate">
                      {ev.type === "lesson_completed" ? `Lezione completata: ${ev.label}` : `${typeLabel[ev.label] || ev.label} — ${ev.score} (${ev.esito})`}
                    </span>
                    <span className="font-mono text-[11px] text-[color:var(--text-2)] shrink-0">{fmtDateTime(ev.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Confirm dialog */}
        <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-extrabold tracking-tight">
                {confirm?.action === "role" && (confirm.value ? "Promuovere ad amministratore?" : "Riportare a studente?")}
                {confirm?.action === "access" && (confirm.value === "paid" ? "Attivare l'accesso completo?" : "Riportare a free?")}
                {confirm?.action === "anonymize" && "Anonimizzare questo utente?"}
                {confirm?.action === "delete" && "Eliminare definitivamente?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirm?.action === "role" && (confirm.value ? "Avrà accesso completo all'area gestionale." : "Perderà l'accesso all'area gestionale.")}
                {confirm?.action === "access" && (confirm.value === "paid" ? "L'utente avrà accesso a tutti i contenuti senza pagare su piattaforma (es. pagamento gestito fuori app)." : "L'utente tornerà allo stato free: al prossimo aggiornamento del gating vedrà solo i contenuti gratuiti.")}
                {confirm?.action === "anonymize" && "I dati personali (nome, email) vengono rimossi e l'accesso disattivato. I risultati restano in forma anonima per le statistiche. Potrai eliminarlo definitivamente in seguito."}
                {confirm?.action === "delete" && "Utente, progressi e tutti i tentativi verranno cancellati per sempre. Operazione irreversibile."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={runAction}
                disabled={busy}
                data-testid="admin-confirm-user-action-button"
                className={`rounded-full font-extrabold ${confirm?.action === "delete" ? "bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90" : ""}`}
              >
                Conferma
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Expiry dialog */}
        <Dialog open={!!expiryDialog} onOpenChange={(o) => !o && setExpiryDialog(null)}>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-extrabold tracking-tight">Scadenza account</DialogTitle>
              <DialogDescription>Sine die = accesso senza limiti di tempo. Con scadenza, l'accesso si blocca automaticamente dopo la data scelta.</DialogDescription>
            </DialogHeader>
            {expiryDialog && (
              <div className="space-y-3">
                <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] px-4 py-3 cursor-pointer">
                  <input
                    type="radio"
                    name="expiry-mode"
                    checked={!expiryDialog.hasExpiry}
                    onChange={() => setExpiryDialog({ ...expiryDialog, hasExpiry: false })}
                    className="w-4 h-4 accent-black"
                    data-testid="admin-expiry-sinedie-radio"
                  />
                  <span className="text-sm font-semibold">Sine die — nessuna scadenza</span>
                </label>
                <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] px-4 py-3 cursor-pointer">
                  <input
                    type="radio"
                    name="expiry-mode"
                    checked={expiryDialog.hasExpiry}
                    onChange={() => setExpiryDialog({ ...expiryDialog, hasExpiry: true })}
                    className="w-4 h-4 accent-black"
                    data-testid="admin-expiry-date-radio"
                  />
                  <span className="text-sm font-semibold">A scadenza:</span>
                  <input
                    type="date"
                    value={expiryDialog.expiresAt}
                    onChange={(e) => setExpiryDialog({ ...expiryDialog, hasExpiry: true, expiresAt: e.target.value })}
                    className="flex-1 rounded-xl border border-[color:var(--hairline)] px-3 py-2 text-sm font-mono outline-none focus:border-[color:var(--ink)]"
                    data-testid="admin-expiry-date-input"
                  />
                </label>
              </div>
            )}
            <DialogFooter>
              <button onClick={() => setExpiryDialog(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
              <button onClick={saveExpiry} disabled={busy} data-testid="admin-save-expiry-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
                {busy ? "Salvataggio…" : "Salva"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-2)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nome o email…"
            data-testid="admin-users-search-input"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={`${inputCls} max-w-[150px]`} data-testid="admin-users-role-filter">
          <option value="">Tutti i ruoli</option>
          <option value="student">Studenti</option>
          <option value="admin">Amministratori</option>
        </select>
        <button
          onClick={() => {
            setNewUser(emptyNewUser);
            setCreateOpen(true);
          }}
          data-testid="admin-create-user-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
        >
          <UserPlus className="w-3.5 h-3.5" /> Nuovo utente
        </button>
        <button
          onClick={() => downloadCsv("/admin/export/users", "iscritti.csv")}
          data-testid="admin-export-users-button"
          className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] text-xs font-bold px-4 py-2.5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {!users && <Skeleton className="h-40 rounded-3xl" />}
      {users && users.length === 0 && (
        <p className="text-sm text-[color:var(--text-2)] py-8 text-center rounded-3xl border border-dashed border-[color:var(--hairline)]">Nessun iscritto trovato.</p>
      )}

      <div className="space-y-2" data-testid="admin-users-list">
        {users?.map((u) => {
          const good = u.lastAttempt && (u.lastAttempt.esito === "IDONEO" || u.lastAttempt.esito === "SUPERATO");
          return (
            <button
              key={u.id}
              onClick={() => openDetail(u.id)}
              data-testid="admin-user-row"
              className="w-full text-left rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold truncate">{u.nome}</p>
                    {u.is_admin && <span className="text-[9px] font-extrabold uppercase rounded-full bg-[color:var(--ink)] text-white px-2 py-0.5">Admin</span>}
                    {u.anonymized && <span className="text-[9px] font-extrabold uppercase rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] px-2 py-0.5">Anonimo</span>}
                    {u.expired && <span className="text-[9px] font-extrabold uppercase rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-2 py-0.5">Scaduto</span>}
                  </div>
                  <p className="text-xs text-[color:var(--text-2)] truncate">{u.email}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-1">
                    Iscritto {fmtDate(u.createdAt)} · ultimo accesso {fmtDate(u.lastAccessAt)} · {u.expiresAt ? `scade ${fmtDate(u.expiresAt)}` : "sine die"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm font-semibold">{u.progressPercent}%</p>
                  <p className="text-[10px] text-[color:var(--text-2)] uppercase font-bold">avanzamento</p>
                  {u.lastAttempt && (
                    <p className={`text-[10px] font-extrabold uppercase mt-1 ${good ? "" : "text-[color:var(--danger)]"}`}>
                      {good ? <span className="rounded-full bg-[color:var(--volt)] px-1.5 py-0.5">{u.lastAttempt.esito}</span> : u.lastAttempt.esito}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-[color:var(--text-2)] shrink-0" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-extrabold tracking-tight">Nuovo utente</DialogTitle>
            <DialogDescription>Crea un account e comunica le credenziali all'iscritto. Potrai modificare ruolo e scadenza in ogni momento.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="block text-xs font-bold mb-1 mt-1">Nome</label>
            <input
              className={inputCls}
              value={newUser.nome}
              onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
              placeholder="Nome e cognome"
              data-testid="admin-new-user-nome-input"
            />
            <label className="block text-xs font-bold mb-1 mt-3">Email</label>
            <input
              type="email"
              className={inputCls}
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="nome@esempio.it"
              data-testid="admin-new-user-email-input"
            />
            <label className="block text-xs font-bold mb-1 mt-3">Password</label>
            <input
              type="text"
              className={inputCls}
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="Minimo 6 caratteri"
              data-testid="admin-new-user-password-input"
            />
            <label className="block text-xs font-bold mb-1 mt-3">Ruolo</label>
            <select
              className={inputCls}
              value={newUser.is_admin ? "admin" : "student"}
              onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.value === "admin" })}
              data-testid="admin-new-user-role-select"
            >
              <option value="student">Studente</option>
              <option value="admin">Amministratore</option>
            </select>

            <label className="block text-xs font-bold mb-1 mt-3">Validità account</label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] px-4 py-3 cursor-pointer">
                <input
                  type="radio"
                  name="new-user-expiry"
                  checked={!newUser.hasExpiry}
                  onChange={() => setNewUser({ ...newUser, hasExpiry: false })}
                  className="w-4 h-4 accent-black"
                  data-testid="admin-new-user-sinedie-radio"
                />
                <span className="text-sm font-semibold">Sine die — nessuna scadenza</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] px-4 py-3 cursor-pointer">
                <input
                  type="radio"
                  name="new-user-expiry"
                  checked={newUser.hasExpiry}
                  onChange={() => setNewUser({ ...newUser, hasExpiry: true })}
                  className="w-4 h-4 accent-black"
                  data-testid="admin-new-user-expiry-radio"
                />
                <span className="text-sm font-semibold">A scadenza:</span>
                <input
                  type="date"
                  value={newUser.expiresAt}
                  onChange={(e) => setNewUser({ ...newUser, hasExpiry: true, expiresAt: e.target.value })}
                  className="flex-1 rounded-xl border border-[color:var(--hairline)] px-3 py-2 text-sm font-mono outline-none focus:border-[color:var(--ink)]"
                  data-testid="admin-new-user-expiry-date-input"
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
            <button onClick={createUser} disabled={busy} data-testid="admin-save-new-user-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
              {busy ? "Creazione…" : "Crea utente"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
